import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';

import { compareSourceSSPArray, rearrangeSourceSSPArray } from '../../../core/helpers';

import { GeneralSchainService } from '.';
import { DropdownInterface, PartnerInterfaceGlobal } from '../../../core/models';
import { AuthService, ErrorsHandlingService, LaravelEchoService } from '../../../core/services';

import { SourceSchainInterface } from '../models';
import { NzModalService } from 'ng-zorro-antd/modal';
import { UnsavedChangesWarningComponent } from '../modals';

@Injectable({
  providedIn: 'root'
})
export class EditSchainService {
  public companies: DropdownInterface.ICompaniesDataGeneralSource[] = [];

  public isAllDisabled = false;

  public editSSPSourceForm: FormGroup;

  public partnerSettings: PartnerInterfaceGlobal.IPartnerSettings;

  public progress = 0;
  public uploadStatus: 'success' | 'exception' | 'active' | 'normal';
  public onShowProgress = false;
  public uploadedFileName = '';
  public isShowFileName = false;
  public onShowCancelButton = false;

  public initialCheckedCompanies: number[] = [];

  private progressListener: any;
  private uploadResultListener: any;
  private failureListener: any;

  private activeIntervals: any[] = [];

  private importId: number;

  private sourceId: number;

  private checkedSSPIdsArray: number[] = [];

  private isUnsavedChanges = false;


  constructor(
    private fb: FormBuilder,
    private generalSchainService: GeneralSchainService,
    private laravelEchoService: LaravelEchoService,
    private authService: AuthService,
    private loadingService: NgxSpinnerService,
    private errorsHandlingService: ErrorsHandlingService,
    private modalService: NzModalService,
  ) { }

  public get fileNameControl(): FormControl {
    return this.editSSPSourceForm.get('fileName') as FormControl;
  }

  public get companiesSSPControl(): FormControl {
    return this.editSSPSourceForm.get('companiesSSP') as FormControl;
  }

  public get isAllSSPControl(): FormControl {
    return this.editSSPSourceForm.get('isAllSSP') as FormControl;
  }

  public isDisabledCancelButton(): boolean {
    return this.uploadStatus === 'active';
  }

  public initComponent(data: {
    sourceData: SourceSchainInterface.ISourceSchain;
    checkedCompanies: number[];
    companies: DropdownInterface.ICompaniesDataGeneralSource[];
  }): void {
    this.createSSPSourceForm();
    this.updateForm(data.sourceData);
    this.uploadedFileName = data.sourceData.fileName;
    this.sourceId = data.sourceData.id;
    this.isShowFileName = true;
    this.onShowCancelButton = false;
    this.onShowProgress = false;
    this.initialCheckedCompanies = data.checkedCompanies;
    this.checkedSSPIdsArray = data.checkedCompanies;
    this.modifyCompanies(data);
  }

  public onUploadFileHandler(event: Event, callback: () => void, type: string): boolean {
    // Unsubscribe from any existing listeners before setting up new ones
    this.onUnsubscribeListeners();

    this.readThis(event.target as HTMLInputElement);
    const file = (event.target as HTMLInputElement).files;
    if (!file || !this.fileExtValid(file) || !file.hasOwnProperty(0)) {
      return false;
    }

    const fileToUpload = file.item(0);
    if (!fileToUpload) {
      return false;
    }

    this.progress = 0;
    this.uploadStatus = 'active';
    let uploadProgress = 0;

    // Ensure WebSocket connection is established
    this.laravelEchoService.echo.connector.pusher.connect();

    // Set up socket listeners before starting the upload
    this.progressListener = this.laravelEchoService.echo
      .private(`App.User.${this.authService.userId}`)
      .listen(
        '.SourceSchainImportProgress',
        (data: { progress: number }) => {
          this.loadingService.hide();
          this.uploadedFileName = '';
          this.isShowFileName = false;
          this.onShowProgress = true;
          this.onShowCancelButton = true;
          uploadProgress += data.progress;

          const interval = setInterval(() => {
            if (this.progress < uploadProgress) {
              this.progress += 1;
            } else {
              clearInterval(interval);
              // Remove interval from tracking array after it's cleared
              const index = this.activeIntervals.indexOf(interval);
              if (index > -1) {
                this.activeIntervals.splice(index, 1);
              }
            }
          }, 100);
          // Track this interval so we can clear it if needed
          this.activeIntervals.push(interval);
        });

    this.uploadResultListener = this.laravelEchoService.echo
      .private(`App.User.${this.authService.userId}`)
      .listen(
        '.SourceSchainImportSuccess',
        (data: {
          success: boolean;
          title: string;
          message: string;
          id: number;
          file_name: string;
        }) => {
          uploadProgress = 0;
          this.progress = 100;
          this.uploadedFileName = data.file_name;
          this.isShowFileName = true;
          this.sourceId = data.id;
          this.isUnsavedChanges = true;
          this.uploadStatus = 'success';
          this.onShowCancelButton = false;
          this.onUnsubscribeListeners();
          this.errorsHandlingService.showNotification('success', 'success', 'success', data.message, '450px');
          this.getPartnerInfo();
        });

    this.failureListener = this.laravelEchoService.echo
      .private(`App.User.${this.authService.userId}`)
      .listen(
        '.SourceSchainImportFailed',
        (data: any) => {
          this.loadingService.hide();
          uploadProgress = 0;
          this.progress = 0;
          this.onShowCancelButton = false;
          this.uploadStatus = 'exception';
          this.onUnsubscribeListeners();
          this.errorsHandlingService.showNotification('error', 'error', 'error', data.message, null);
          this.getPartnerInfo();
        });



    const fileName = this.fileNameControl.value;

    this.loadingService.show();
    this.generalSchainService.getUploadFileRequest(fileToUpload, type, fileName, this.sourceId)
      .pipe(
        finalize(() => {
          callback();
        })
      )
      .subscribe(
        (res) => {
          this.importId = res.importId;
        },
        error => {
          this.loadingService.hide();
          this.onUnsubscribeListeners();
          this.onShowProgress = false;
          this.progress = 0;
          this.uploadStatus = 'normal';
          this.onShowCancelButton = false;
          this.errorsHandlingService.handleError(error);
        }
      );

    return true;
  }

  public onCancelUploadHandler(type: string): void {
    this.onUnsubscribeListeners();

    this.loadingService.show();
    this.generalSchainService.getCancelUploadSourceSchainRequest(this.importId, type)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.progress = 0;
        this.onShowCancelButton = false;
        this.uploadedFileName = '';
        this.isShowFileName = false;
        this.uploadStatus = 'normal';
        this.onShowProgress = false;
        this.getPartnerInfo();
      },
        error => this.errorsHandlingService.handleError(error));
  }

  public onDownloadFileHandler(): void {
    this.loadingService.show();
    this.generalSchainService.getDownloadSourceSchainFileRequest(this.sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(res => {
        window.location.href = res.file;
      },
        error => this.errorsHandlingService.handleError(error));
  }

  public onDeleteFileHandler(): void {
    this.loadingService.show();
    this.generalSchainService.getDeleteSourceSchainFileRequest(this.sourceId)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.isUnsavedChanges = true;
        this.errorsHandlingService.showNotification('success', 'success', 'success', 'File successfully deleted!', null);
        this.uploadedFileName = '';
        this.isShowFileName = false;
        this.getPartnerInfo();
      },
        error => this.errorsHandlingService.handleError(error));
  }

  public onCompaniesHandler(data: { isAll: boolean, items: number[], hasChanges: boolean, checkedEndpoint?: { [key: string]: any } }): void {
    this.isAllSSPControl.patchValue(data.isAll ? 1 : 0);
    this.checkedSSPIdsArray = data.items;
    this.companies = compareSourceSSPArray(
      this.companies,
      this.checkedSSPIdsArray,
      this.isAllSSPControl.value,
      this.initialCheckedCompanies,
    );
    this.companiesSSPControl.patchValue(this.checkedSSPIdsArray, { emitEvent: false });
  }

  public onCloseModalHandler(callback: (value: boolean) => void): void {
    if (this.isUnsavedChanges) {
      this.onShowUnsavedChangesModal(callback);
    } else {
      callback(false);
      this.sourceId = null;
    }
  }

  public onUpdateSourceSchainHandler(callback: () => void, type: string): void {
    if (!this.handleErrors(type)) {
      return;
    }

    this.loadingService.show();
    this.generalSchainService.getSendSourceSchainRequest(
      this.sourceId,
      type === 'ssp' ? this.companiesSSPControl.value : undefined,
      this.fileNameControl.value
    )
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.errorsHandlingService.showNotification('success', 'success', 'success', 'Source successfully updated!', null);
        callback();

        this.editSSPSourceForm.reset({ emitEvent: false });
      },
        error => this.errorsHandlingService.handleError(error));
  }

  private createSSPSourceForm(): void {
    this.editSSPSourceForm = this.fb.group({
      fileName: [null, [Validators.required]],
      companiesSSP: [[]],
      isAllSSP: [false],
    });
  }

  private updateForm(data: SourceSchainInterface.ISourceSchain): void {
    this.editSSPSourceForm.patchValue({
      fileName: data.fileName,
      companiesSSP: data.company,
      isAllSSP: false,
    }, { emitEvent: false });
  }

  private handleErrors(type: string): boolean {
    let errorMessages: string[] = [];


    errorMessages = this.validateFileName(errorMessages);

    if (type === 'ssp') {
      errorMessages = this.validateCompaniesSSP(errorMessages);
    }

    if (errorMessages.length) {
      errorMessages.forEach(errorMessage => this.errorsHandlingService.showNotification('error', 'error', 'error', errorMessage, '410px'));
      return false;
    }
    return true;
  }


  private validateFileName(errorMessages: string[]): string[] {
    if (this.fileNameControl.errors?.required) {
      errorMessages.push('File name is required');
    }
    return errorMessages;
  }


  private validateCompaniesSSP(errorMessages: string[]): string[] {
    if (this.companiesSSPControl.value.length === 0) {
      errorMessages.push('Please select at least one SSP company ');
    }

    return errorMessages;
  }

  private onUnsubscribeListeners(): void {
    this.activeIntervals.forEach(interval => clearInterval(interval));
    this.activeIntervals = [];

    if (this.progressListener || this.uploadResultListener || this.failureListener) {
      this.laravelEchoService.echo.leave(`App.User.${this.authService.userId}`);
      this.progressListener = null;
      this.uploadResultListener = null;
      this.failureListener = null;
    }
  }

  private onShowUnsavedChangesModal(callback: (value: boolean) => void): void {
    const dialogRef = this.modalService.create({
      nzContent: UnsavedChangesWarningComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'deactivation-warning-modal',
      nzWrapClassName: 'deactivation-warning-modal-wrap',
      nzStyle: { width: '570px', height: 'auto' },
      nzBodyStyle: { width: '570px', height: 'auto', overflow: 'hidden', padding: '0px' },
      nzCentered: true,
      nzFooter: null,
    });

    dialogRef.afterClose.subscribe(res => {
      if (res) {
        callback(true);
        this.sourceId = null;
      }
    });
  }

  private readThis(inputValue: HTMLInputElement): void {
    if (!inputValue.files?.length) {
      return;
    }

    const file: File = inputValue.files[0];
    switch (Boolean(file)) {
      case true:
        const reader: FileReader = new FileReader();
        reader.onloadend = () => {
          (<string>reader.result).split('\n').map(line => {
            return line;
          });
        };
        reader.readAsText(file);
        break;
      case false:
        break;
    }
  }

  private fileExtValid(file: FileList): boolean {
    const fileName = file.item(0)?.name;

    if (!fileName) {
      return false;
    }

    const fileExt = fileName.slice((Math.max(0, fileName.lastIndexOf('.')) || Infinity) + 1);
    if (!['csv', 'txt'].includes(fileExt)) {
      this.errorsHandlingService.showNotification(
        'error',
        'error',
        'error',
        'Unsupported file format. Please upload files in csv format',
        '400px',
      );
      return false;
    }

    return true;
  }

  private modifyCompanies(data: {
    sourceData: SourceSchainInterface.ISourceSchain;
    checkedCompanies: number[];
    companies: DropdownInterface.ICompaniesDataGeneralSource[];
  }): void {
    this.companies = this.generalSchainService.disableCheckedCompanies(data.checkedCompanies, data.companies);
    this.companies = this.generalSchainService.updateCompaniesForEditSSPSource(data.sourceData.company, this.companies);

    this.isAllDisabled = this.companies.every(company => company.disabled);

    // this.isAllSSPControl.patchValue(isAllChecked ? true : false, { emitEvent: false });

    this.companies = this.getRearrangedSSPArray(
      this.companies,
      data.sourceData.company,
      this.isAllSSPControl.value,
    );
  }

  private getRearrangedSSPArray(
    inputArray: DropdownInterface.ICompaniesDataGeneralSource[],
    inputIdsArray: number[],
    isAll: boolean,
  ): DropdownInterface.ICompaniesDataGeneralSource[] {
    const uniqueEndpointsMap = new Map<number, DropdownInterface.ICompaniesDataGeneralSource>();

    const comparedSSPArray = compareSourceSSPArray(inputArray, inputIdsArray, isAll, this.initialCheckedCompanies);

    const rearrangedSSPArray = rearrangeSourceSSPArray(comparedSSPArray, inputIdsArray);

    rearrangedSSPArray.forEach((company) => {
      uniqueEndpointsMap.set(company.id, company);
    });

    const modifiedSSPArray = Array.from(uniqueEndpointsMap.values());

    return modifiedSSPArray;
  }

  private getPartnerInfo(): void {
    this.authService.getAllPartnerInfo()
      .subscribe(res => {
        this.partnerSettings = res as any;
        this.laravelEchoService.initEcho(this.authService.token);

        this.uploadStatus = 'normal';
        this.progress = 0;
        this.onShowProgress = false;
      },
        error => this.errorsHandlingService.handleError(error));
  }
}
